import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import "./input.css";
const Dropdown = ({
  label,
  value,
  onChange,
  data = [],
  placeholder,
  onFocus,
  mess,
  error,
  options,
  mw,
  w,
  onBlur,
  disabled = false,
  width2 = "100%",
  fontSize = "14px",
  required,
  searchable = false,
  sortable = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);

  useEffect(() => {
    const updatePosition = () => {
      if (!triggerRef.current) return;

      const rect = triggerRef.current.getBoundingClientRect();
      const dropdownHeight = 260;
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUpward = spaceBelow < dropdownHeight;

      setDropdownPosition({
        top: openUpward
          ? rect.top + window.scrollY - dropdownHeight - 4
          : rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    };

    if (isOpen) {
      updatePosition();
      window.addEventListener("scroll", updatePosition, true);
      window.addEventListener("resize", updatePosition);
    }

    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isOpen]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isOpen &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Convert options children to data array if searchable/sortable mode
  const getProcessedOptions = () => {
    // If using options prop (React children), extract them
    if (options) {
      const optionArray = [];
      // Handle React fragments and nested children
      const extractChildren = (children) => {
        React.Children.forEach(children, (child) => {
          if (!child) return;
          // If it's an option element
          if (child.type === 'option' && child.props) {
            optionArray.push({
              value: child.props.value,
              label: child.props.children,
            });
          }
          // If it has nested children (like Fragment), recurse
          else if (child.props && child.props.children) {
            extractChildren(child.props.children);
          }
        });
      };
      extractChildren(options);
      return optionArray;
    }
    return data;
  };

  let processedData = getProcessedOptions();

  // Filter out empty value options (like "SELECT" placeholder) for searchable dropdown
  if (searchable) {
    processedData = processedData.filter((item) => item.value !== "" && item.value !== undefined && item.value !== null);
  }

  if (sortable) {
    processedData = [...processedData].sort((a, b) => {
      const titleA = a.label ? a.label.toString() : "";
      const titleB = b.label ? b.label.toString() : "";
      return titleA.localeCompare(titleB);
    });
  }

  if (searchable && searchTerm) {
    processedData = processedData.filter((item) => {
      const title = item.label ? item.label.toString().toLowerCase() : "";
      return title.includes(searchTerm.toLowerCase());
    });
  }

  // Helper to find currently selected label
  const allOptions = getProcessedOptions();
  const selectedItem = allOptions.find((item) => item.value == value);
  const displayLabel = selectedItem ? selectedItem.label : "SELECT";

  // Handle selection in searchable mode
  const handleSelect = (selectedValue) => {
    // Create a synthetic event to match native select onChange signature
    const syntheticEvent = {
      target: {
        value: selectedValue,
      },
    };
    onChange(syntheticEvent);
    setIsOpen(false);
    setSearchTerm("");
  };

  // Render dropdown list using portal
  const renderDropdownList = () => {
    if (!isOpen) return null;

    return createPortal(
      <div
        ref={dropdownRef}
        style={{
          position: "fixed",
          top: dropdownPosition.top,
          left: dropdownPosition.left,
          width: dropdownPosition.width,
          backgroundColor: "white",
          border: "1px solid #dbdfea",
          borderRadius: "6px",
          zIndex: 20,
          boxShadow: "0 10px 30px rgba(0,0,0,0.18)",
          overflow: "hidden",
          boxSizing: "border-box",
        }}
      >
        {/* Search Input */}
        <div
          style={{
            padding: "8px",
            borderBottom: "1px solid #eee",
            position: "sticky",
            top: 0,
            backgroundColor: "white",
            zIndex: 1,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: "100%",
              padding: "6px 8px",
              border: "1px solid #dbdfea",
              borderRadius: "2px",
              fontSize: fontSize,
              boxSizing: "border-box",
              outline: "none",
              fontFamily: "'IBM Plex Sans', sans-serif",
            }}
            autoFocus
          />
        </div>

        {/* Options List */}
        <div
          style={{
            maxHeight: "220px",
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          {processedData.length > 0 ? (
            processedData.map((item, index) => (
              <div
                key={index}
                title={item.label}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelect(item.value);
                }}
                style={{
                  padding: "8px 12px",
                  cursor: "pointer",
                  backgroundColor:
                    item.value == value ? "#e3f2fd" : "transparent",
                  borderBottom: "1px solid #f5f5f5",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  userSelect: "none",
                  fontFamily: "'IBM Plex Sans', sans-serif",
                  fontSize: fontSize,
                }}
                onMouseEnter={(e) => {
                  if (item.value != value) {
                    e.target.style.backgroundColor = "#f5f5f5";
                  }
                }}
                onMouseLeave={(e) => {
                  if (item.value != value) {
                    e.target.style.backgroundColor = "transparent";
                  }
                }}
              >
                {item.label}
              </div>
            ))
          ) : (
            <div
              style={{
                padding: "12px",
                textAlign: "center",
                color: "#999",
                fontFamily: "'IBM Plex Sans', sans-serif",
                fontSize: fontSize,
              }}
            >
              No results found
            </div>
          )}
        </div>
      </div>,
      document.body
    );
  };

  // Render searchable custom dropdown
  if (searchable) {
    return (
      <div style={{ width: width2 }}>
        <div className="inp-main">
          {label && (
            <div className="inp-label">
              {label?.toUpperCase()}{" "}
              {required && <span style={{ color: "red" }}>*</span>}{" "}
            </div>
          )}
        </div>

        <div
          ref={triggerRef}
          className="drop-select searchable-dropdown"
          style={{
            minWidth: mw || "220px",
            width: w || "100%",
            fontSize: fontSize,
            position: "relative",
            cursor: disabled ? "not-allowed" : "pointer",
            opacity: disabled ? 0.6 : 1,
            backgroundColor: "#fff",
            height: "35px",
            display: "flex",
            alignItems: "center",
            paddingLeft: "10px",
            paddingRight: "28px",
            border: "1px solid #dbdfea",
            borderRadius: "2px",
            // backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23666' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`,
            backgroundRepeat: "no-repeat",
            backgroundPosition: "calc(100% - 8px) center",
          }}
          onClick={() => !disabled && setIsOpen(!isOpen)}
        >
          <div
            style={{
              width: "100%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {displayLabel}
          </div>
        </div>

        {renderDropdownList()}

        {mess && <div className="inp-mess">{mess}</div>}
        {error && <div className="inp-error">{error}</div>}
      </div>
    );
  }

  // Default native select mode (backward compatible)
  return (
    <div style={{ width: width2 }} ref={dropdownRef}>
      <div className="inp-main">
        {label && (
          <div className="inp-label">
            {label?.toUpperCase()}{" "}
            {required && <span style={{ color: "red" }}>*</span>}{" "}
          </div>
        )}
      </div>


      <select
        disabled={disabled}
        onBlur={onBlur}
        onFocus={onFocus}
        className={"drop-select"}
        style={{
          minWidth: mw || "120px",
          width: w || "100%",
          fontSize: fontSize,
        }}
        value={value}
        onChange={onChange}
      >
        {options || (
          <>
            <option value={""}>SELECT</option>
            {processedData.map((v, i) => (
              <option key={i} value={v.value}>
                {v.label}
              </option>
            ))}
          </>
        )}
      </select>

      {mess && <div className="inp-mess">{mess}</div>}
      {error && <div className="inp-error">{error}</div>}
    </div>
  );
};

export default Dropdown;
